import React, { useEffect, useState } from 'react';
import { useFeathers } from '../../app/util';
import { SearchableSelectInput } from '../SearchableSelectInput';
import InputAdornment from '@mui/material/InputAdornment';

/**
 * DistrictSelectInput - A searchable select input for choosing districts
 * 
 * @param {Object} props
 * @param {District|null} [props.value=null] - The selected district object (full District object, NOT just an ID).
 *                                             Should be null if no district is selected.
 * @param {Function} props.onChange - Callback function called when a district is selected.
 *                                     Receives an event object: onChange(e)
 *                                     The selected district object is available at e.target.value
 *                                     Example: onChange={(e) => { const district = e.target.value; ... }}
 * @param {District|null} [props.descendantOf=null] - Optional parent district to filter results
 * @param {string|null} [props.state=null] - Optional state district ID to filter results
 * @param {Object} [props.style={}] - Optional styling object
 * @param {string[]} [props.districtTypes] - Optional array of district type strings to filter districts by (e.g. ['county', 'city'])
 * @param {boolean} [props.multi=false] - Whether multiple districts can be selected
 * @param {string|null} [props.primaryDistrictId=null] - If multi is true, this district ID will be excluded from options
 * @param {string[]} [props.seedMultiSearchText=[]] - Pre-populated search text
 * @param {boolean} [props.clearAfterSelect=false] - Whether to clear search text after selection
 * @param {string} [props.placeholder='Select a district'] - Placeholder text for the search input
 * @param {React.ReactNode} [props.startAdornment] - Optional start adornment (e.g. icon) inside the input
 *
 * @example
 * // Single selection
 * const [district, setDistrict] = useState(null);
 * <DistrictSelectInput
 *   value={district}
 *   onChange={(e) => setDistrict(e.target.value)}
 *   districtTypes={['county', 'city']}
 * />
 */
const DistrictSelectInput = ({
  value,
  onChange,
  descendantOf = null,
  state = null,
  style = {},
  districtTypes,
  multi = false,
  primaryDistrictId = null,
  seedMultiSearchText = [],
  clearAfterSelect = false,
  placeholder = 'Select a district',
  startAdornment = null
}) => {
  const feathers = useFeathers();

  const [ optionsForField, setOptionsForField ] = useState([]);
  const [ loadingForField, setLoadingForField ] = useState([]);  // an array of timestamps
  const [ loading, setLoading ] = useState(false);
  const [ queuedResultsForField, setQueuedResultsForField ] = useState(null);

  const searchForField = async (searchText) => {
    if(!feathers) return;

    const timestamp = new Date().getTime();
    setLoadingForField(l => [...l, timestamp]);
    setLoading(true)

    const districts = await feathers.getService('districts').find({ query: {
      ...(searchText ? { searchTerm: { $search: searchText } } : {}),
      ...(districtTypes?.length ? { type: districtTypes } : {}),
      ...(state != null ? { state } : {}),
      ...(descendantOf != null ? { $descendantOf: descendantOf, $nestChildren: false, $includeAncestor: true } : {}),
    }})
    let options = districts.data.map(({ longName, _id, votingType, ...rest }) => ({ text: `${longName}${votingType === 'representative-district' ? ' (at-large)' : ''}`, _id, longName, ...rest }));
    if (multi && primaryDistrictId) {
      options = options.filter(({ _id }) => _id !== primaryDistrictId);
    }

    setQueuedResultsForField({
      timestamp,
      options
    })
  }

  useEffect(() => {
    if(queuedResultsForField) {
      setQueuedResultsForField(null);

      const mostRecentTimestamp = loadingForField[loadingForField.length - 1];
      const isMostRecentResult = mostRecentTimestamp === queuedResultsForField.timestamp;
      if(!isMostRecentResult) return;

      // we do this so that we only acknowledge the most recent timestamp
      setOptionsForField(queuedResultsForField.options);
      setLoading(false);
    }
  }, [queuedResultsForField, loadingForField ])

  return (
    <SearchableSelectInput
      name='district'
      onChangeSearchText={(searchText) => searchForField(searchText)}
      value={value}
      onChange={onChange}
      optionsLoading={loading}
      options={optionsForField || []}
      style={style}
      multi={multi}
      clearAfterSelect={clearAfterSelect}
      placeholder={placeholder}
      InputProps={
        startAdornment
          ? { startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment> }
          : undefined
      }
    />
  )
}

export { DistrictSelectInput };
